/* AdminDomains — single ops surface listing every tenant's domain state.
 * Visible at /admin/domains. Lets the admin spot stalled DNS verifications
 * and stale custom_domain rows at a glance.
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { ShieldCheck, AlertCircle, Globe, Loader2, ExternalLink } from "lucide-react";
import AdminDomain360Drawer from "../../components/AdminDomain360Drawer";

const STATUS_TONE = {
  active:                  "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  pending_dns_verification:"bg-amber-500/15 text-amber-700 border-amber-500/30",
  pending_ssl:             "bg-sky-500/15 text-sky-700 border-sky-500/30",
  failed:                  "bg-rose-500/15 text-rose-700 border-rose-500/30",
};

export default function AdminDomains() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTid, setSelectedTid] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/domains");
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return (
    <div className="p-8"><Loader2 className="w-5 h-5 animate-spin text-foreground/40"/></div>
  );

  const customCount = data.rows.filter(r => r.custom_domain).length;
  const subCount    = data.rows.filter(r => r.auto_subdomain).length;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-domains-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Domains</h1>
          <p className="text-[13.5px] text-foreground/60 mt-1">Every tenant's vanity host configuration at a glance.</p>
        </div>
        <button onClick={load} className="text-[12.5px] text-foreground/60 hover:text-foreground" data-testid="admin-domains-refresh">Refresh</button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <Stat k="Tenants" v={data.rows.length}/>
        <Stat k="With subdomain" v={subCount}/>
        <Stat k="With custom domain" v={customCount}/>
      </div>

      {!data.cloudflare_configured && (
        <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-[13px] text-amber-900 flex items-start gap-2.5" data-testid="admin-domains-cf-not-configured">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0"/>
          <div>
            <strong>Cloudflare for SaaS not configured.</strong> Custom domains are accepted in "manual" mode — sellers
            see DNS instructions but no certificate is auto-issued. Set <code>CLOUDFLARE_API_TOKEN</code>, <code>CLOUDFLARE_ZONE_ID</code>,{" "}
            <code>CLOUDFLARE_FALLBACK_ORIGIN</code>, and <code>CLOUDFLARE_CNAME_TARGET</code> in the backend env to activate the integration.
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-white overflow-hidden">
        <table className="w-full text-[13px]" data-testid="admin-domains-table">
          <thead>
            <tr className="text-left text-[11.5px] uppercase tracking-wide text-foreground/55 bg-secondary/40">
              <th className="px-4 py-3">Tenant</th>
              <th className="px-4 py-3">Subdomain</th>
              <th className="px-4 py-3">Custom domain</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Provider</th>
              <th className="px-4 py-3">Auto-config</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.rows.map((r) => (
              <tr key={r.tenant_id}
                  onClick={() => setSelectedTid(r.tenant_id)}
                  className="hover:bg-foreground/5 cursor-pointer transition-colors"
                  data-testid={`admin-domain-row-${r.slug}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.tenant_name}</div>
                  <div className="text-foreground/50 text-[11.5px] font-mono">{r.slug}</div>
                </td>
                <td className="px-4 py-3 font-mono text-[12px]">
                  {r.auto_subdomain ? (
                    <a href={`https://${r.auto_subdomain}`} target="_blank" rel="noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       className="inline-flex items-center gap-1 text-foreground/85 hover:text-foreground">
                      {r.auto_subdomain} <ExternalLink className="w-3 h-3"/>
                    </a>
                  ) : <span className="text-foreground/40">—</span>}
                </td>
                <td className="px-4 py-3 font-mono text-[12px]">
                  {r.custom_domain ? (
                    <a href={`https://${r.custom_domain}`} target="_blank" rel="noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       className="inline-flex items-center gap-1 text-foreground/85 hover:text-foreground">
                      {r.custom_domain} <ExternalLink className="w-3 h-3"/>
                    </a>
                  ) : <span className="text-foreground/40">—</span>}
                </td>
                <td className="px-4 py-3">
                  {r.custom_domain_status ? (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11.5px] font-medium ${STATUS_TONE[r.custom_domain_status] || "bg-foreground/5 text-foreground/65 border-foreground/15"}`}>
                      {r.custom_domain_status === "active" ? <ShieldCheck className="w-3 h-3"/> : <Globe className="w-3 h-3"/>}
                      {r.custom_domain_status.replace(/_/g, " ")}
                    </span>
                  ) : <span className="text-foreground/40">—</span>}
                </td>
                <td className="px-4 py-3 text-foreground/65">
                  {r.custom_domain_provider || <span className="text-foreground/40">—</span>}
                </td>
                <td className="px-4 py-3" title={r.auto_config_error || ""}>
                  {r.auto_config_provider ? (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${
                      r.auto_config_success
                        ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/30"
                        : "bg-rose-500/15 text-rose-700 border-rose-500/30"
                    }`}>
                      {r.auto_config_success ? "✓" : "✕"} {r.auto_config_provider}
                    </span>
                  ) : <span className="text-foreground/40 text-[11.5px]">manual</span>}
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-foreground/55 text-[13px]">No tenants yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminDomain360Drawer
        tenantId={selectedTid}
        onClose={() => setSelectedTid(null)}
        onChanged={load}
      />
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="text-[11px] uppercase tracking-wide text-foreground/55">{k}</div>
      <div className="text-2xl font-semibold mt-1.5 tracking-tight">{v}</div>
    </div>
  );
}
